import { useState } from "react";
import {
  CheckSquare,
  CopyPlus,
  FileSignature,
  FileText,
  GripVertical,
  LayoutTemplate,
  PanelTop,
  PenSquare,
  Plus,
  Rows3,
  Trash2,
} from "lucide-react";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import type { ProposalDocumentLayoutPage, TemplatePageType } from "../../types";

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

type PaletteItem = {
  type: TemplatePageType;
  shortLabel: string;
  label: string;
  icon: typeof LayoutTemplate;
};

const PAGE_PALETTE: PaletteItem[] = [
  { type: "cover", shortLabel: "Cover", label: "Cover Page", icon: PanelTop },
  { type: "cover_letter", shortLabel: "Letter", label: "Cover Letter", icon: FileText },
  { type: "line_items", shortLabel: "Pricing", label: "Services & Pricing", icon: Rows3 },
  { type: "custom", shortLabel: "Custom", label: "Custom Page", icon: PenSquare },
  { type: "closing", shortLabel: "Closing", label: "Closing Page", icon: CheckSquare },
  { type: "signature", shortLabel: "Signature", label: "Signature Page", icon: FileSignature },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pageTypeChipClass(type?: string) {
  switch (type) {
    case "cover":
      return "border-sky-400/25 bg-sky-400/10 text-sky-300";
    case "cover_letter":
      return "border-violet-400/25 bg-violet-400/10 text-violet-300";
    case "line_items":
      return "border-emerald-400/25 bg-emerald-400/10 text-emerald-300";
    case "static_page":
      return "border-amber-400/25 bg-amber-400/10 text-amber-300";
    case "closing":
      return "border-pink-400/25 bg-pink-400/10 text-pink-300";
    case "signature":
      return "border-cyan-400/25 bg-cyan-400/10 text-cyan-300";
    default:
      return "border-white/15 bg-white/[0.05] text-white/50";
  }
}

function pageTypeShortLabel(type?: string) {
  return PAGE_PALETTE.find((i) => i.type === type)?.shortLabel ?? "Page";
}

// ---------------------------------------------------------------------------
// SortablePageItem
// ---------------------------------------------------------------------------

interface SortablePageItemProps {
  page: ProposalDocumentLayoutPage;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  readOnly: boolean;
}

function SortablePageItem({
  page,
  index,
  isSelected,
  onSelect,
  onDuplicate,
  onDelete,
  readOnly,
}: SortablePageItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `pg:${page.id ?? ""}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [hovered, setHovered] = useState(false);
  const sectionCount = Array.isArray(page.sections) ? page.sections.length : 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group relative rounded-xl border transition-all",
        isSelected
          ? "border-white/25 bg-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.04)]"
          : "border-white/8 bg-white/[0.025] hover:border-white/15 hover:bg-white/[0.05]",
        isDragging && "z-50 opacity-30",
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex items-start gap-2 px-2.5 py-2.5">
        {/* Drag handle */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          disabled={readOnly}
          title="Drag to reorder"
          className="mt-0.5 cursor-grab touch-none text-white/20 transition hover:text-white/50 active:cursor-grabbing disabled:cursor-default"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>

        {/* Page info */}
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={onSelect}
        >
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] tabular-nums text-white/28">{index + 1}</span>
            <p className="truncate text-xs font-medium leading-4 text-white/82">
              {page.title || "Untitled Page"}
            </p>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5">
            <span
              className={cn(
                "rounded-full border px-1.5 py-0.5 text-[9px] uppercase tracking-[0.1em]",
                pageTypeChipClass(page.type),
              )}
            >
              {pageTypeShortLabel(page.type)}
            </span>
            <span className="text-[10px] text-white/30">
              {sectionCount} section{sectionCount === 1 ? "" : "s"}
            </span>
          </div>
        </button>

        {/* Hover actions */}
        <div
          className={cn(
            "flex shrink-0 items-center gap-0.5 transition-opacity",
            hovered || isSelected ? "opacity-100" : "opacity-0",
          )}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
            disabled={readOnly}
            title="Duplicate page"
            className="rounded-lg p-1 text-white/28 transition hover:bg-white/[0.06] hover:text-white/70 disabled:opacity-30"
          >
            <CopyPlus className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            disabled={readOnly}
            title="Delete page"
            className="rounded-lg p-1 text-white/28 transition hover:bg-red-500/10 hover:text-red-400 disabled:opacity-30"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CanvasLeftSidebar
// ---------------------------------------------------------------------------

export interface InheritedStaticPage {
  id: string;
  title: string;
  contentHtml: string;
}

export interface CanvasLeftSidebarProps {
  pages: ProposalDocumentLayoutPage[];
  selectedPageId: string | null;
  onSelectPage: (id: string) => void;
  onAddPage: (type: TemplatePageType) => void;
  onDuplicatePage: (id: string) => void;
  onDeletePage: (id: string) => void;
  readOnly?: boolean;
  inheritedPages?: InheritedStaticPage[];
  onAddInheritedPage?: (page: InheritedStaticPage) => void;
}

export function CanvasLeftSidebar({
  pages,
  selectedPageId,
  onSelectPage,
  onAddPage,
  onDuplicatePage,
  onDeletePage,
  readOnly = false,
  inheritedPages = [],
  onAddInheritedPage,
}: CanvasLeftSidebarProps) {
  const pageIds = pages.map((p) => `pg:${p.id ?? ""}`);

  return (
    <aside className="flex w-[260px] shrink-0 flex-col overflow-hidden border-r border-white/8 bg-[#0c0d0f]">
      {/* Sidebar header */}
      <div className="border-b border-white/8 px-4 py-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/35">
          Pages
        </p>
      </div>

      {/* Add page palette */}
      <div className="border-b border-white/8 px-3 py-3">
        <p className="mb-2 text-[10px] uppercase tracking-[0.14em] text-white/28">
          Add page
        </p>
        <div className="grid grid-cols-2 gap-1.5">
          {PAGE_PALETTE.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.type}
                type="button"
                disabled={readOnly}
                onClick={() => onAddPage(item.type)}
                title={item.label}
                className="flex items-center gap-2 rounded-xl border border-white/8 bg-white/[0.025] px-2.5 py-2 text-left transition hover:border-white/15 hover:bg-white/[0.06] disabled:opacity-40"
              >
                <div className="shrink-0 rounded-lg border border-white/10 bg-white/[0.04] p-1.5 text-white/45">
                  <Icon className="h-3 w-3" />
                </div>
                <span className="truncate text-[11px] text-white/62">
                  {item.shortLabel}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Page list */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        <p className="mb-2 text-[10px] uppercase tracking-[0.14em] text-white/28">
          {pages.length} page{pages.length === 1 ? "" : "s"}
        </p>

        {pages.length === 0 ? (
          <div className="flex min-h-[120px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-white/8 px-4 py-6 text-center">
            <LayoutTemplate className="h-5 w-5 text-white/18" />
            <p className="text-[11px] leading-5 text-white/30">
              Use the grid above to add your first page
            </p>
          </div>
        ) : (
          <SortableContext items={pageIds} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5">
              {pages.map((page, index) => (
                <SortablePageItem
                  key={page.id}
                  page={page}
                  index={index}
                  isSelected={page.id === selectedPageId}
                  onSelect={() => onSelectPage(page.id ?? "")}
                  onDuplicate={() => onDuplicatePage(page.id ?? "")}
                  onDelete={() => onDeletePage(page.id ?? "")}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </SortableContext>
        )}
        {/* Brand static pages — click "+" to add as editable DnD page */}
        {inheritedPages.length > 0 && (
          <div className="mt-3 border-t border-white/8 pt-3">
            <p className="mb-2 text-[10px] uppercase tracking-[0.14em] text-white/28">
              Brand pages
            </p>
            <div className="space-y-1.5">
              {inheritedPages.map((page) => (
                <div
                  key={page.id}
                  className="flex items-center gap-2 rounded-xl border border-white/6 bg-white/[0.02] px-2.5 py-2"
                >
                  <p className="min-w-0 flex-1 truncate text-[11px] text-white/55">{page.title}</p>
                  <button
                    type="button"
                    disabled={readOnly || !onAddInheritedPage}
                    onClick={() => onAddInheritedPage?.(page)}
                    title="Add to canvas"
                    className="shrink-0 rounded-lg p-1 text-white/30 transition hover:bg-white/[0.08] hover:text-white/70 disabled:opacity-30"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
